import PageMain from '@/components/page-main';
import {
  getXiaohongshuAccounts,
  initXiaohongshuRobot,
  getWorkflowsByPlatform,
  connectWorkflow,
} from '@/services/xiaohongshu';
import {
  Table,
  Space,
  Button,
  message,
  Badge,
  Tag,
  Modal,
  Radio,
  Popover,
  Select,
} from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { useState, useEffect, useMemo, useReducer } from 'react';
import {
  history,
  useSelector,
  useDispatch,
  Access,
  useAccess,
  useModel,
} from 'umi';
import { TimeComponent, buttonContainerStyle } from '@/pages/workflow/index';
import ActionButtons from '@/pages/xiaohongshu/components/actionButtons';
import { SettingOutlined } from '@ant-design/icons';
import fetchValueReducer from '../../hooks/fetchValueReducer';
import useFetch from '@/hooks/useFetch';
import clientSocket from '@/services/clientSocket';
import Login from './components/login';

const Index: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const currentUser = initialState?.currentUser;

  const [fetchValue, dispatchFetchValue] = useReducer(fetchValueReducer, {
    page: 1,
    size: 10,
    total: 1,
    name: currentUser!.name,
  });
  const { data, loading, fetchData } = useFetch(
    '/local/xiaohongshu',
    fetchValue,
    dispatchFetchValue,
  );
  const [robotsProcess, setRobotsProcess] = useState<API.RobotsProcess>({});
  const [timer, setTimer] = useState<NodeJS.Timer>();
  const [visible, setVisible] = useState<boolean>(false);
  const [mode, setMode] = useState<number>(1);
  const [currentAccount, setCurrentAccount] =
    useState<API.XiaohongshuAccount>();
  const [workflows, setWorkflows] = useState<
    { label: string; value: number }[]
  >([]);
  const [currentWorkflow, setCurrentWorkflow] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [qrcode, setQrcode] = useState<string | null>(null);
  const [loginPhase, setLoginPhase] = useState<string>('');
  const [currentLoginAction, setCurrentLoginAction] = useState<{
    action: string;
    xiaohongshuNo: string;
    xiaohongshuName: string;
  } | null>(null);

  const socket = useSelector((state: any) => state.socketIO.socket);
  const dispatch = useDispatch();

  const access = useAccess();

  useEffect(() => {
    if (!socket) dispatch({ type: 'socketIO/init' });
    init();
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('robotsProcesses', (args: any) => {
        setRobotsProcess(args);
      });
      socket.emit('getRobotsProcesses');
      const timer = setInterval(() => {
        socket.emit('getRobotsProcesses');
      }, 20000);
      setTimer(timer);
    }
    return () => {
      if (socket) {
        socket.off('robotsProcesses');
      }
      if (timer) clearInterval(timer);
    };
  }, [socket]);

  const robotsList = useMemo(() => {
    if (!data.length) return [];
    return data.map((d: API.XiaohongshuAccount) => {
      const process = robotsProcess[d.xiaohongshuNo];
      return {
        ...d,
        status: process?.status ?? 'stopped',
        startTime: process?.startTime ?? '',
        stopTime: process?.stopTime ?? '',
        restartTime: process?.restartTime ?? '',
      };
    });
  }, [robotsProcess, data]);

  const optionsModes = [
    { label: '原模式', value: 1 },
    { label: '编排模式', value: 2 },
  ];

  const columns: ColumnsType<API.XiaohongshuAccount & API.RobotProcess> = [
    {
      title: 'ID',
      dataIndex: 'xiaohongshuNo',
      key: 'xiaohongshuNo',
      width: 180,
    },
    {
      title: '官号',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <div>{name ? name : '-'}</div>,
      width: 180,
    },
    {
      title: '话术模式',
      dataIndex: 'mode',
      key: 'mode',
      width: 180,
      render: (_: 1 | 2, record) => {
        const config = {
          1: {
            label: '原流程',
            color: 'green',
          },
          2: {
            label: '编排流程',
            color: 'pink',
          },
        };
        const value = config[_];
        return (
          <Space>
            <Tag color={value.color}>{value.label}</Tag>
            <Popover
              title="更改话术模式"
              content="原流程：由官号管理模块所设置的话术流程；编排流程：由流程配置模块所设置的话术流程。"
            >
              <Access accessible={access.xiaohongshuRobot}>
                <SettingOutlined onClick={() => modifyMode(record)} />
              </Access>
            </Popover>
          </Space>
        );
      },
    },
    {
      title: '关联的编排流程',
      dataIndex: 'workflowName',
      key: 'workflowName',
      width: 180,
      render: (_) => (_ ? _ : '-'),
    },
    {
      title: '机器人状态',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (_: 'running' | 'stopped' | 'error', record) => {
        let statusMap: { [key: string]: 'success' | 'default' | 'warning' } = {
          running: 'success',
          stopped: 'default',
          error: 'warning',
        };
        return <Badge status={statusMap[_]} text={_} />;
      },
    },
    {
      title: '机器人操作',
      width: 180,
      render: (_, record) => {
        return (
          <Access accessible={access.xiaohongshuRobot}>
            <ActionButtons
              status={record.status}
              xiaohongshuNo={record.xiaohongshuNo}
              xiaohongshuName={record.name}
              buttonClick={handleActionButtonClick}
            />
          </Access>
        );
      },
    },
    // {
    //   title: '机器人状态变更记录',
    //   width: 300,
    //   render: (_, record) => {
    //     let arr = [['启动时间', record.startTime], ['停止时间', record.stopTime], ['重启时间', record.restartTime]];
    //     return <>
    //       {
    //         arr.map((vo) => <p key={vo[0]}>{vo[0]}:{vo[1].length ? vo[1] : '-'}</p>)
    //       }
    //     </>
    //   }
    // },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (_) => TimeComponent(_),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (_) => TimeComponent(_),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Access accessible={access.xiaohongshuEdit}>
            <a onClick={() => history.push(`/xiaohongshu/${_.id}`)}>编辑</a>
          </Access>
        </Space>
      ),
    },
  ];

  /**@description 更改机器人话术模式 */
  const modifyMode = (record: API.XiaohongshuAccount) => {
    setCurrentAccount(record);
    setMode(record.mode);
    if (record.workflowId) setCurrentWorkflow(record.workflowId);
    setVisible(true);
  };

  const clearModeModal = () => {
    setVisible(false);
    setMode(1);
    setCurrentWorkflow(null);
  };

  /** @description 提交关联 */
  const submitConnect = async () => {
    if (mode === 2 && !currentWorkflow)
      return message.warning('请选择关联的流程！');
    if (!currentAccount) return;
    const { resp } = await connectWorkflow({
      id: currentAccount?.id,
      workflowId: currentWorkflow ?? undefined,
      mode,
    });
    console.log('res==>', resp);
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    fetchData();
    clearModeModal();
  };

  const handleCopy = (id: number) => {};

  // 使用 useEffect 管理 clientSocket 监听器
  useEffect(() => {
    const handleQrcode = (qrcode: string) => {
      setQrcode(qrcode);
      setLoginPhase('qrcode');
    };

    const handleError = (errorMessage: string) => {
      setIsLoading(false);
      alert(errorMessage);
      setCurrentLoginAction(null);
    };

    const handleLoginSuccess = () => {
      setIsLoading(false);
      setLoginPhase('success');
      if (socket && currentLoginAction) {
        socket.emit(
          'handleRobotAction',
          currentLoginAction.action,
          currentLoginAction.xiaohongshuNo,
          currentLoginAction.xiaohongshuName,
        );
      }
      setCurrentLoginAction(null);
    };

    if (currentLoginAction) {
      clientSocket.socket.on('qrcode', handleQrcode);
      clientSocket.socket.on('error', handleError);
      clientSocket.socket.on('login-success', handleLoginSuccess);
    }

    return () => {
      clientSocket.socket.off('qrcode', handleQrcode);
      clientSocket.socket.off('error', handleError);
      clientSocket.socket.off('login-success', handleLoginSuccess);
    };
  }, [currentLoginAction, socket]);

  const handleActionButtonClick = (
    action: string,
    xiaohongshuNo: string,
    xiaohongshuName: string,
  ) => {
    if (action === 'stop') {
      socket.emit('handleRobotAction', action, xiaohongshuNo, xiaohongshuName);
      return;
    }

    setQrcode(null);
    setIsLoading(true);
    setLoginPhase('init');
    setCurrentLoginAction({ action, xiaohongshuNo, xiaohongshuName });
    clientSocket.socket.emit('sxtLogin', { xiaohongshuNo });
    // socket.emit('handleRobotAction', action, xiaohongshuNo, xiaohongshuName); // temp, to be deleted
  };

  const init = async () => {
    const res = await getWorkflowsByPlatform({ platform: 2 });
    setWorkflows(
      res.resp?.data.map((vo: API.Workflow) => {
        return { label: vo.name, value: vo.id };
      }),
    );
  };

  const initRobots = async () => {
    const { resp, error } = await initXiaohongshuRobot();
    if (resp?.code !== 0) return message.error(error?.message);
    message.success(resp.message);
    fetchData();
  };

  return (
    <>
      {isLoading && <Login loginPhase={loginPhase} qrcode={qrcode} />}
      <PageMain>
        <Space style={buttonContainerStyle}>
          {/* <Button type="primary" onClick={initRobots}>
          初始化机器人配置
        </Button> */}
          <Access accessible={access.xiaohongshuCreate}>
            <Button
              type="primary"
              onClick={() => history.push('/xiaohongshu/:id')}
            >
              新建官号
            </Button>
          </Access>
        </Space>
        <Table
          loading={loading}
          columns={columns}
          dataSource={robotsList}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            current: fetchValue.page,
            total: fetchValue.total,
            pageSize: fetchValue.size,
            pageSizeOptions: [10, 20, 50],
            onChange: (page, size) => {
              dispatchFetchValue({ type: 'update', value: { page, size } });
              fetchData();
            },
          }}
        />

        <Modal
          visible={visible}
          onCancel={clearModeModal}
          onOk={submitConnect}
          title={`更改${currentAccount?.name}机器人话术模式`}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio.Group
              options={optionsModes}
              onChange={(e) => setMode(e.target.value)}
              value={mode}
              optionType="button"
              buttonStyle="solid"
            />

            {mode === 2 && (
              <Select
                style={{ width: '100%' }}
                placeholder="请选择要关联的流程"
                options={workflows}
                value={currentWorkflow}
                onChange={(e) => setCurrentWorkflow(e)}
              />
            )}
          </Space>
        </Modal>
      </PageMain>
    </>
  );
};

export default Index;
